"use client";

/*
 * Page building blocks for the platform site, per the Tulus brand guidelines
 * (October 2026): a pale header-mist welcome, Poppins hierarchy, the 4 x 18 px
 * cyan marker beside section headings, white 18 px cards with a fine border
 * and soft shadow (theme MuiCard), and cyan kept for icons and markers.
 * Text colours come from the text-safe shades in ../brand.ts.
 */

import { Box, Button, Card, Container, Typography } from "@mui/material";
import type { SvgIconComponent } from "@mui/icons-material";
import Link from "next/link";
import { TULUS } from "../brand";

export type Action = { label: string; href: string };

const isExternal = (href: string) => /^https?:/.test(href);

export function ActionButton({ action, variant = "contained" }: { action: Action; variant?: "contained" | "outlined" }) {
  const sx = variant === "outlined" ? { borderColor: TULUS.cyanText, color: TULUS.cyanText, backgroundColor: TULUS.paper } : undefined;
  if (isExternal(action.href)) {
    return (
      <Button variant={variant} size="large" href={action.href} target="_blank" rel="noopener noreferrer" sx={sx}>
        {action.label}
      </Button>
    );
  }
  return (
    <Button variant={variant} size="large" component={Link} href={action.href} prefetch={false} sx={sx}>
      {action.label}
    </Button>
  );
}

const textLinkSx = { color: TULUS.cyanText, fontWeight: 500, textDecoration: "none", "&:hover": { textDecoration: "underline" } };

export function TextLink({ action }: { action: Action }) {
  if (isExternal(action.href)) {
    return (
      <Box component="a" href={action.href} target="_blank" rel="noopener noreferrer" sx={textLinkSx}>
        {action.label} →
      </Box>
    );
  }
  return (
    <Box component={Link} href={action.href} prefetch={false} sx={textLinkSx}>
      {action.label} →
    </Box>
  );
}

export function PageHero({
  title,
  promise,
  lead,
  actions = [],
  aside,
}: {
  title: string;
  promise?: string;
  lead: string[];
  actions?: Action[];
  /** Optional picture beside the text on wide screens, below it on phones. */
  aside?: React.ReactNode;
}) {
  return (
    <Box component="section" sx={{ backgroundColor: TULUS.mist, borderBottom: `1px solid ${TULUS.line}` }}>
      <Container
        maxWidth="lg"
        sx={{
          py: { xs: 6, md: aside ? 7 : 10 },
          display: aside ? "grid" : "block",
          gridTemplateColumns: { xs: "1fr", md: "7fr 5fr" },
          gap: { xs: 4, md: 6 },
          alignItems: "center",
        }}
      >
        <Box sx={{ maxWidth: 760 }}>
          <Typography
            variant="h1"
            sx={{ fontSize: { xs: "2.1rem", md: "3rem" }, lineHeight: 1.15, color: TULUS.ink, textWrap: "balance", mb: promise ? 1.5 : 2.5 }}
          >
            {title}
          </Typography>
          {promise && (
            <Typography sx={{ fontSize: { xs: "1.15rem", md: "1.35rem" }, fontWeight: 500, color: TULUS.cyanText, mb: 2.5 }}>
              {promise}
            </Typography>
          )}
          {lead.map((p) => (
            <Typography key={p} sx={{ fontSize: { xs: "1.02rem", md: "1.12rem" }, color: TULUS.ink, lineHeight: 1.7, mb: 1.5, maxWidth: 680 }}>
              {p}
            </Typography>
          ))}
          {actions.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mt: 3.5 }}>
              {actions.map((a, i) => (
                <ActionButton key={a.href + a.label} action={a} variant={i === 0 ? "contained" : "outlined"} />
              ))}
            </Box>
          )}
        </Box>
        {aside && <Box sx={{ justifySelf: "center", width: "100%", maxWidth: { xs: 380, md: 480 } }}>{aside}</Box>}
      </Container>
    </Box>
  );
}

/** A section heading with the guide's 4 x 18 px cyan marker beside it. */
export function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
      <Box aria-hidden sx={{ width: 4, height: 18, borderRadius: 2, backgroundColor: TULUS.cyan, flexShrink: 0 }} />
      <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "1.9rem" }, color: TULUS.ink, textWrap: "balance" }}>
        {children}
      </Typography>
    </Box>
  );
}

export function Section({
  title,
  intro = [],
  tone = "paper",
  id,
  children,
}: {
  title?: string;
  intro?: string[];
  tone?: "paper" | "mist";
  /** Anchor for in-page links. */
  id?: string;
  children?: React.ReactNode;
}) {
  return (
    <Box
      component="section"
      id={id}
      sx={{ backgroundColor: tone === "mist" ? TULUS.mist : TULUS.paper, py: { xs: 6, md: 9 }, scrollMarginTop: 16 }}
    >
      <Container maxWidth="lg">
        {title && <SectionHeading>{title}</SectionHeading>}
        {intro.map((p) => (
          <Typography key={p} sx={{ color: TULUS.ink, fontSize: "1.05rem", lineHeight: 1.75, maxWidth: 720, mb: 2 }}>
            {p}
          </Typography>
        ))}
        {children && <Box sx={{ mt: intro.length ? 4 : 1 }}>{children}</Box>}
      </Container>
    </Box>
  );
}

/** The guide's service icon recipe: cyan outline icon, 27 px in a 52 px container. */
export function ServiceIcon({ icon: Icon }: { icon: SvgIconComponent }) {
  return (
    <Box
      aria-hidden
      sx={{ width: 52, height: 52, borderRadius: "14px", backgroundColor: TULUS.mist, display: "grid", placeItems: "center", flexShrink: 0 }}
    >
      <Icon sx={{ fontSize: 27, color: TULUS.cyan }} />
    </Box>
  );
}

export type CardItem = { title: string; body: string; kicker?: string; icon?: SvgIconComponent };

export function CardGrid({ items, columns = 3 }: { items: CardItem[]; columns?: 2 | 3 }) {
  return (
    <Box
      sx={{
        display: "grid",
        gap: 2.5,
        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: `repeat(${columns}, 1fr)` },
      }}
    >
      {items.map((item) => (
        <Card key={item.title} component="article" sx={{ p: 3, display: "flex", flexDirection: "column", gap: 1.25 }}>
          {item.icon && <ServiceIcon icon={item.icon} />}
          {item.kicker && (
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 500, letterSpacing: "0.06em", textTransform: "uppercase", color: TULUS.cyanText }}>
              {item.kicker}
            </Typography>
          )}
          <Typography variant="h3" sx={{ fontSize: "1.12rem", color: TULUS.ink }}>
            {item.title}
          </Typography>
          <Typography sx={{ color: TULUS.mutedText, lineHeight: 1.65, fontSize: "0.97rem" }}>{item.body}</Typography>
        </Card>
      ))}
    </Box>
  );
}

/** An ordered sequence: only for content that really is a series of steps. */
export function Steps({ items }: { items: { title?: string; body: string }[] }) {
  return (
    <Box component="ol" sx={{ listStyle: "none", p: 0, m: 0, display: "grid", gap: 2, maxWidth: 760 }}>
      {items.map((s, i) => (
        <Box component="li" key={s.body} sx={{ display: "grid", gridTemplateColumns: "40px 1fr", gap: 2, alignItems: "start" }}>
          <Box
            aria-hidden
            sx={{ width: 36, height: 36, borderRadius: "50%", border: `2px solid ${TULUS.cyan}`, color: TULUS.cyanText, display: "grid", placeItems: "center", fontWeight: 700 }}
          >
            {i + 1}
          </Box>
          <Typography sx={{ color: TULUS.ink, lineHeight: 1.65, pt: 0.75 }}>
            {s.title && <Box component="strong" sx={{ fontWeight: 700 }}>{s.title}: </Box>}
            {s.body}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

export function QuestionList({ items }: { items: { q: string; a: string }[] }) {
  return (
    <Box sx={{ display: "grid", gap: 2, maxWidth: 820 }}>
      {items.map((f) => (
        <Box key={f.q} sx={{ borderBottom: `1px solid ${TULUS.line}`, pb: 2 }}>
          <Typography variant="h3" sx={{ fontSize: "1.05rem", color: TULUS.ink, mb: 0.75 }}>
            {f.q}
          </Typography>
          <Typography sx={{ color: TULUS.mutedText, lineHeight: 1.65 }}>{f.a}</Typography>
        </Box>
      ))}
    </Box>
  );
}

/** The closing invitation at the foot of a page. */
export function CtaBand({ title, body, action }: { title: string; body: string; action: Action }) {
  return (
    <Box component="section" sx={{ backgroundColor: TULUS.mist, borderTop: `1px solid ${TULUS.line}`, py: { xs: 6, md: 8 } }}>
      <Container maxWidth="md" sx={{ textAlign: "center" }}>
        <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "1.9rem" }, color: TULUS.ink, textWrap: "balance", mb: 1.5 }}>
          {title}
        </Typography>
        <Typography sx={{ color: TULUS.ink, fontSize: "1.05rem", lineHeight: 1.7, mb: 3.5, maxWidth: 620, mx: "auto" }}>
          {body}
        </Typography>
        <ActionButton action={action} />
      </Container>
    </Box>
  );
}
